import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, ArrowRight, Building2, GraduationCap, BookOpen, ClipboardList, Award, Newspaper,
  Cog, Briefcase, Stethoscope, Scale, Palette, Music2, Atom, TrendingUp, MapPin, Star, Quote,
  CheckCircle2, Sparkles, Users, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import CollegeCard from '../components/CollegeCard';
import { TESTIMONIALS, CITIES, STATS, CATEGORIES, STREAMS } from '../data/mock';
import { fetchColleges, fetchExams, fetchBlogs } from '../lib/api';

const ICON_MAP = { Building2, GraduationCap, BookOpen, ClipboardList, Award, Newspaper, Cog, Briefcase, Stethoscope, Scale, Palette, Music2, Atom, TrendingUp };

const Home = () => {
  const [colleges, setColleges] = useState([]);
  const [exams, setExams] = useState([]);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    fetchColleges().then(setColleges).catch(() => {});
    fetchExams().then(setExams).catch(() => {});
    fetchBlogs().then(setBlogs).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-teal-50 via-white to-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-teal-200/40 rounded-full blur-3xl" />
          <div className="absolute top-40 -left-40 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 pt-14 pb-16 md:pt-20 md:pb-24 grid lg:grid-cols-2 gap-10 items-center">
          <div className="fade-in-up">
            <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 border-teal-200 mb-5">
              <Sparkles className="w-3 h-3 mr-1" /> #1 Education Discovery Platform
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Find the right <span className="gradient-text">college, course &amp; exam</span> for your future.
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-xl">
              Compare 35,000+ colleges, explore courses and prepare for top entrance exams  -  all in one place, backed by verified reviews.
            </p>

            {/* Search Card */}
            <div className="mt-8 bg-white rounded-2xl shadow-xl border border-slate-200 p-3">
              <div className="flex flex-col sm:flex-row items-stretch gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    placeholder="Search colleges, exams, courses..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm"
                  />
                </div>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12 px-6 rounded-xl">
                  Search <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-3 px-1">
                <span className="text-xs text-slate-500">Popular:</span>
                {['IIT Bombay', 'CAT 2025', 'B.Tech CSE', 'NEET', 'MBA'].map((t) => (
                  <Link to="/colleges" key={t} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-teal-50 hover:text-teal-700 text-slate-600 transition-colors">
                    {t}
                  </Link>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
              {STATS.map((s) => (
                <div key={s.label} className="text-center sm:text-left">
                  <div className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{s.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero visual: stacked cards */}
          <div className="relative hidden lg:block">
            <div className="relative w-full aspect-[5/4]">
              <img
                src="https://images.unsplash.com/photo-1607013407627-6ee814329547?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Students"
                className="absolute inset-0 w-full h-full object-cover rounded-3xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 w-56 border border-slate-100">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 grid place-items-center">
                    <CheckCircle2 className="w-5 h-5 text-teal-700" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Application Sent</div>
                    <div className="font-semibold text-slate-900 text-sm">IIT Bombay</div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-4 w-60 border border-slate-100">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-orange-600" />
                  <div className="text-xs font-semibold text-slate-700">18L+ students helped</div>
                </div>
                <div className="flex -space-x-2">
                  {TESTIMONIALS.slice(0, 4).map((t, i) => (
                    <img key={i} src={t.avatar} alt={t.name} className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY TILES */}
      <section className="max-w-7xl mx-auto px-4 py-14">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {CATEGORIES.map((c) => {
            const Icon = ICON_MAP[c.icon];
            return (
              <Link
                key={c.key}
                to={c.key === 'blogs' ? '/blog' : `/${c.key}`}
                className="group relative bg-white border border-slate-200 rounded-2xl p-5 card-lift text-center"
              >
                <div className="w-12 h-12 mx-auto rounded-xl bg-teal-50 grid place-items-center group-hover:bg-teal-600 transition-colors">
                  <Icon className="w-6 h-6 text-teal-700 group-hover:text-white transition-colors" />
                </div>
                <div className="mt-3 font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.label}</div>
                <div className="text-xs text-slate-500 mt-1">{c.count}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* STREAMS BAR */}
      <section className="bg-slate-50 py-14 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Explore by stream</h2>
              <p className="text-slate-600 mt-1">Discover programs and colleges by your field of interest.</p>
            </div>
            <Link to="/courses" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-orange-600">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {STREAMS.map((s) => {
              const Icon = ICON_MAP[s.icon];
              return (
                <Link key={s.key} to="/colleges" className={`${s.color} rounded-xl p-4 text-center card-lift`}>
                  <Icon className="w-6 h-6 mx-auto" />
                  <div className="mt-2 text-sm font-semibold">{s.label}</div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TOP COLLEGES */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 border-orange-200 mb-2">Top Rated</Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Top Colleges in India 2025</h2>
          </div>
          <Link to="/colleges" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-orange-600">
            See all colleges <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {colleges.slice(0, 8).map((c) => (
            <CollegeCard key={c.id} college={c} />
          ))}
        </div>
      </section>

      {/* EXAMS STRIP */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <Badge className="bg-teal-500/20 text-teal-300 hover:bg-teal-500/20 border-teal-500/30 mb-2">Upcoming</Badge>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Popular Entrance Exams</h2>
              <p className="text-slate-400 mt-1">Dates, patterns, cut-offs  -  all in one place.</p>
            </div>
            <Link to="/exams" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-teal-400 hover:text-orange-400">
              All exams <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {exams.slice(0, 6).map((e) => (
              <Link key={e.id} to={`/exams/${e.slug || e.id}`} className="group bg-slate-800/60 hover:bg-slate-800 border border-slate-700 rounded-2xl p-5 flex gap-4 items-start card-lift">
                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                  <img src={e.image} alt={e.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg group-hover:text-teal-300 transition-colors">{e.name}</h3>
                    <Badge className="bg-orange-500/20 text-orange-300 hover:bg-orange-500/20 border-orange-500/30 text-[10px]">{e.level}</Badge>
                  </div>
                  <p className="text-xs text-slate-400 line-clamp-2 mt-1">{e.description}</p>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    <div><span className="text-slate-500">Next Date:</span> <span className="text-white font-semibold">{e.nextDate}</span></div>
                    <div><span className="text-slate-500">Mode:</span> <span className="text-white font-semibold">{e.mode}</span></div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Popular Study Destinations</h2>
          <p className="text-slate-600 mt-2">Explore top education hubs across India.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {CITIES.map((c) => (
            <Link key={c.name} to="/colleges" className="group bg-white border border-slate-200 rounded-xl p-5 card-lift">
              <div className="flex items-center justify-between">
                <MapPin className="w-5 h-5 text-teal-600" />
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-orange-500 group-hover:translate-x-1 transition-all" />
              </div>
              <div className="mt-4 font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">{c.colleges.toLocaleString()} colleges</div>
            </Link>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-gradient-to-br from-teal-50 to-orange-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-white text-teal-800 hover:bg-white border-teal-200 mb-2">Student Voices</Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Trusted by lakhs of students &amp; parents</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm card-lift">
                <Quote className="w-6 h-6 text-teal-200" />
                <p className="text-sm text-slate-700 mt-3 leading-relaxed">{t.quote}</p>
                <div className="mt-4 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.role}</div>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 mt-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className={`w-3.5 h-3.5 ${i < t.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BLOG STRIP */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 border-teal-200 mb-2">Insights</Badge>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>From the Learndunia Blog</h2>
          </div>
          <Link to="/blog" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-orange-600">
            All articles <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogs.slice(0, 3).map((b) => (
            <Link key={b.id} to={`/blog/${b.slug || b.id}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200 card-lift">
              <div className="aspect-[16/9] overflow-hidden">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold">{b.category}</span>
                  <span className="text-slate-500">· {b.readTime}</span>
                </div>
                <h3 className="mt-3 font-semibold text-slate-900 text-lg leading-snug group-hover:text-teal-700 transition-colors line-clamp-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{b.title}</h3>
                <p className="mt-2 text-sm text-slate-600 line-clamp-2">{b.excerpt}</p>
                <div className="mt-4 text-xs text-slate-500">By {b.author} · {b.date}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-600 to-teal-700 text-white p-8 md:p-12">
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-orange-400/30 rounded-full blur-3xl pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <ShieldCheck className="w-10 h-10 mb-4 text-orange-300" />
              <h3 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Get free 1:1 counselling</h3>
              <p className="mt-3 text-teal-50">Speak to our expert counsellors and get personalised college recommendations based on your rank, budget and career goals.</p>
            </div>
            <div className="bg-white rounded-2xl p-5 text-slate-900 shadow-2xl">
              <div className="grid grid-cols-1 gap-3">
                <input placeholder="Full Name" className="px-4 py-3 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                <input placeholder="Mobile Number" className="px-4 py-3 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                <input placeholder="Preferred Stream (e.g. Engineering)" className="px-4 py-3 rounded-lg border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm" />
                <Button className="bg-orange-500 hover:bg-orange-600 text-white h-11 mt-1">Request Callback</Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
